import React from 'react';
import Button from '@mui/material/Button';
import Box from '@mui/material/Box';
import { BET_ALREADY_MESSAGE, getAnswerColor, getBetDisplayLabel } from '../betLabels';
import { calculateCancelPreview } from '../betCancelPreview';

function BetChoiceCardSP({
  displayLabel,
  colorSet: providedColorSet,
  index,
  rate = '-',
  betPoint,
  betDetail = null,
  pendingPoint = 0,
  canBetTime,
  isHighlighted,
  disabledBySideLock,
  sideLockMessage = BET_ALREADY_MESSAGE,
  betEntryStatus = 'notBet',
  onConfirmBet,
  onCancelConfirmedBet,
  isBetSubmitting = false,
  isBetCancelling = false,
  canCancelConfirmedBet = false,
}) {
  const colorSet = providedColorSet || getAnswerColor(index);
  const label = displayLabel || getBetDisplayLabel(index);
  const currentBetPoint = Number(betPoint || 0);
  const currentPendingPoint = Number(pendingPoint || 0);
  const currentBetRate = betDetail?.rate ?? betDetail?.option_rate ?? null;
  const numericCurrentBetRate = Number(currentBetRate);
  const numericLiveRate = Number(rate);
  const displayBetRate = Number.isFinite(numericCurrentBetRate) ? numericCurrentBetRate.toFixed(2) : rate;
  const expectedPayout = betDetail?.expected_payout ?? null;
  const { refundPoint: cancelRefundPoint } = calculateCancelPreview(currentBetPoint);
  const isBusy = isBetSubmitting || isBetCancelling;
  const isBetPlaced = betEntryStatus === 'placed';
  const canCommitPending = currentPendingPoint > 0 && canBetTime && !disabledBySideLock && !isBetPlaced && !isBusy;
  const showCommitButton = !canCancelConfirmedBet && !isBetPlaced;
  const useSingleLineLayout = currentBetPoint <= 0 && showCommitButton;
  const showRateUpCue =
    canCancelConfirmedBet &&
    currentBetPoint > 0 &&
    Number.isFinite(numericCurrentBetRate) &&
    Number.isFinite(numericLiveRate) &&
    numericLiveRate > numericCurrentBetRate;

  const betButtonSx = {
    fontSize: '12px',
    fontWeight: 800,
    padding: '5px 10px',
    minWidth: '86px',
    height: '32px',
    backgroundColor: colorSet.primary,
    color: '#fff',
    boxShadow: 'none',
    textTransform: 'none',
    whiteSpace: 'nowrap',
    '&:hover': {
      backgroundColor: colorSet.hover,
      boxShadow: '0 3px 6px rgba(0,0,0,0.15)',
    },
  };

  const cancelConfirmedButtonSx = {
    fontSize: '12px',
    fontWeight: 900,
    padding: '5px 10px',
    minWidth: '96px',
    height: '32px',
    color: showRateUpCue ? '#d84315' : '#37474f',
    borderColor: showRateUpCue ? '#ff8a2d' : 'rgba(55, 71, 79, 0.58)',
    backgroundColor: showRateUpCue ? '#fff8e1' : 'rgba(96, 125, 139, 0.08)',
    boxShadow: showRateUpCue ? '0 0 0 2px rgba(255, 138, 45, 0.18)' : 'none',
    textTransform: 'none',
    transition: 'border-color 0.2s ease, background-color 0.2s ease, color 0.2s ease, box-shadow 0.2s ease',
    '&:hover': {
      borderColor: showRateUpCue ? '#f4511e' : '#263238',
      backgroundColor: showRateUpCue ? '#fff3cd' : 'rgba(96, 125, 139, 0.16)',
      boxShadow: showRateUpCue ? '0 0 0 2px rgba(255, 138, 45, 0.24)' : 'none',
    },
  };

  return (
    <Box
      sx={{
        position: 'relative',
        mb: useSingleLineLayout ? 0.75 : 1,
        fontSize: '13px',
        p: useSingleLineLayout ? '8px 10px' : 1.15,
        borderRadius: '12px',
        border: `1px solid ${isHighlighted ? colorSet.primary : colorSet.border}`,
        backgroundColor: disabledBySideLock && currentBetPoint <= 0 ? 'rgba(0,0,0,0.04)' : colorSet.surface,
        opacity: disabledBySideLock && currentBetPoint <= 0 ? 0.58 : 1,
        boxShadow: isHighlighted ? `0 0 0 2px ${colorSet.border}, 0 0 18px ${colorSet.border}` : 'none',
        transition: 'opacity 0.2s ease, box-shadow 0.2s ease, border-color 0.2s ease',
      }}
    >
      {useSingleLineLayout ? (
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: 'minmax(0, 1fr) auto auto',
            alignItems: 'center',
            gap: 0.85,
          }}
        >
          <Box
            component="h3"
            sx={{
              fontSize: '15px',
              margin: 0,
              color: colorSet.primary,
              lineHeight: 1.25,
              fontWeight: 900,
              minWidth: 0,
            }}
          >
            {label}
          </Box>
          <Box sx={{ color: colorSet.primary, fontSize: '12px', fontWeight: 900, whiteSpace: 'nowrap' }}>
            {rate}倍
          </Box>
          <Button
            disabled={!canCommitPending}
            variant="contained"
            onClick={() => onConfirmBet(index)}
            sx={betButtonSx}
          >
            ベットする
          </Button>
        </Box>
      ) : (
        <>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: 1,
              mb: 0.75,
            }}
          >
            <Box sx={{ minWidth: 0 }}>
              <Box
                component="h3"
                sx={{
                  fontSize: '15px',
                  margin: 0,
                  color: colorSet.primary,
                  lineHeight: 1.25,
                  fontWeight: 900,
                }}
              >
                {label}
              </Box>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, flexShrink: 0 }}>
              <Box sx={{ color: colorSet.primary, fontSize: '12px', fontWeight: 900, whiteSpace: 'nowrap' }}>
                {rate}倍
              </Box>
              {currentBetPoint > 0 && (
                <Box
                  component="span"
                  sx={{
                    px: 0.8,
                    py: 0.2,
                    borderRadius: 999,
                    fontSize: '10px',
                    fontWeight: 800,
                    color: colorSet.primary,
                    bgcolor: '#fff',
                    border: `1px solid ${colorSet.border}`,
                    whiteSpace: 'nowrap',
                  }}
                >
                  選択済み
                </Box>
              )}
            </Box>
          </Box>

          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'flex-end',
              gap: 0.75,
            }}
          >
            {canCancelConfirmedBet ? (
              <Box
                sx={{
                  width: '100%',
                  minHeight: '38px',
                  display: 'flex',
                  flexWrap: 'wrap',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  gap: 0.75,
                }}
              >
                <Box
                  component="img"
                  src="/rate-up-popup-candidate.png"
                  width={260}
                  height={195}
                  alt="レートUP中！"
                  aria-hidden={!showRateUpCue}
                  sx={{
                    display: 'block',
                    width: '92px',
                    maxWidth: '29%',
                    height: 'auto',
                    flexShrink: 0,
                    visibility: showRateUpCue ? 'visible' : 'hidden',
                    opacity: showRateUpCue ? 1 : 0,
                    transition: 'opacity 0.2s ease',
                    pointerEvents: 'none',
                  }}
                />
                <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 0.75, justifyContent: 'flex-end', ml: 'auto', minWidth: 0 }}>
                  <Button
                    disabled={isBusy}
                    variant="outlined"
                    onClick={() => onCancelConfirmedBet?.()}
                    sx={cancelConfirmedButtonSx}
                  >
                    ベット取消
                  </Button>
                  <Box sx={{ fontSize: '11px', fontWeight: 800, color: '#455a64', whiteSpace: 'nowrap' }}>
                    {cancelRefundPoint}pt返却
                  </Box>
                </Box>
              </Box>
            ) : showCommitButton && (
              <Button
                disabled={!canCommitPending}
                variant="contained"
                onClick={() => onConfirmBet(index)}
                sx={betButtonSx}
              >
                ベットする
              </Button>
            )}
          </Box>
        </>
      )}

      {currentBetPoint > 0 && (
        <Box
          sx={{
            mt: 0.75,
            p: '6px 8px',
            borderRadius: '7px',
            bgcolor: '#fff',
            border: `1px solid ${colorSet.border}`,
            color: colorSet.primary,
            fontSize: '11px',
            fontWeight: 800,
            lineHeight: 1.4,
            overflowWrap: 'anywhere',
          }}
        >
          確定 {currentBetPoint} pt ・ {displayBetRate}倍 ・ 的中時 {expectedPayout ?? '-'} pt
        </Box>
      )}

      {disabledBySideLock && !canCancelConfirmedBet && (
        <Box sx={{ mt: 1, fontSize: '12px', color: 'rgba(0,0,0,0.62)', fontWeight: 700 }}>
          {sideLockMessage}
        </Box>
      )}
    </Box>
  );
}

export default BetChoiceCardSP;
